import { useState, type FormEvent } from 'react'
import { Button, Chip, Input, Radio, RadioGroup, TextArea } from '@heroui/react'
import { api, ApiError } from '../lib/api'
import { connectionProblem, normaliseGroup, type MappingRow } from '../lib/directory'
import { roleLabel } from '../lib/roleNames'
import type { Branch, DirectorySettings, DirectoryTestResult, OrgRole } from '../lib/types'
import { Icon } from '../ui/icons'
import { shortGroup } from './connectParts'
import { Toggle } from './sectionKit'
import { InnerPage, Notice, SettingBlock, SettingsCard } from './settingsKit'
import '../styles/people.css'

/**
 * The owner-only parts of Directory sign-in: the connection dialog and trying a
 * sign-in. English on purpose (Ruling 8), like the owner's other screens.
 */

const refusal = (err: unknown) => (err instanceof ApiError ? err.message : 'The server could not be reached.')

// ---------------------------------------------------------------------------
// The connection.

/** The fields this drawer edits. Kept separate from DirectorySettings, which
 *  also carries bindPasswordSet, bindPasswordUnreadable and updatedAt — facts
 *  about the stored row, not values a form holds in a draft. */
interface ConnectionDraft {
  url: string
  startTls: boolean
  caPem: string
  bindDn: string
  userTemplate: string
  baseDn: string
  userFilter: string
  displayNameAttr: string
  groupAttr: string
  nestedGroups: boolean
  createAccounts: boolean
  offlineGraceHours: number
  enabled: boolean
}

const BLANK_DRAFT: ConnectionDraft = {
  url: '',
  startTls: false,
  caPem: '',
  bindDn: '',
  userTemplate: '',
  baseDn: '',
  userFilter: '',
  displayNameAttr: 'displayName',
  groupAttr: 'memberOf',
  nestedGroups: false,
  createAccounts: false,
  // The server's default for a directory never told, and the install guide's: 0
  // would switch the outage fallback off before anybody chose that.
  offlineGraceHours: 72,
  enabled: false,
}

const draftOf = (d: DirectorySettings): ConnectionDraft => ({
  url: d.url,
  startTls: d.startTls,
  caPem: d.caPem ?? '',
  bindDn: d.bindDn ?? '',
  userTemplate: d.userTemplate ?? '',
  baseDn: d.baseDn,
  userFilter: d.userFilter,
  displayNameAttr: d.displayNameAttr,
  groupAttr: d.groupAttr,
  nestedGroups: d.nestedGroups,
  createAccounts: d.createAccounts,
  offlineGraceHours: d.offlineGraceHours,
  enabled: d.enabled,
})

export function ConnectionDrawer({
  back,
  organisation,
  saved,
  onClose,
  onSaved,
}: {
  /** The section's name, the page's way back. */
  back: string
  organisation?: string
  /** What is stored now, or null when no directory has been saved yet. */
  saved: DirectorySettings | null
  onClose: () => void
  onSaved: (directory: DirectorySettings) => void
}) {
  const [draft, setDraft] = useState<ConnectionDraft>(saved ? draftOf(saved) : BLANK_DRAFT)
  // A service account binds as somebody and needs its password; a sign-in
  // template binds as the person themselves and stores nothing. The server tells
  // them apart the same way: bindDn set, or userTemplate set, never both.
  const [mode, setMode] = useState<'service' | 'template'>(
    saved?.bindDn ? 'service' : saved?.userTemplate ? 'template' : 'service',
  )
  const [bindPassword, setBindPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const set = (patch: Partial<ConnectionDraft>) => setDraft((d) => ({ ...d, ...patch }))

  const problem = connectionProblem(draft.url, draft.startTls)
  const plainLdap = /^ldap:\/\//i.test(draft.url.trim())

  function switchMode(next: 'service' | 'template') {
    setMode(next)
    // The two are exclusive on the server, so choosing one clears what the other
    // would have sent — a bindDn left behind after switching to a template would
    // still read as "service account" to Problems.
    if (next === 'service') set({ userTemplate: '' })
    else {
      set({ bindDn: '' })
      setBindPassword('')
    }
  }

  async function save() {
    setBusy(true)
    setError('')
    try {
      const { directory } = await api.saveDirectory({
        organisation,
        url: draft.url.trim(),
        startTls: draft.startTls,
        caPem: draft.caPem.trim(),
        bindDn: draft.bindDn.trim(),
        userTemplate: draft.userTemplate.trim(),
        baseDn: draft.baseDn.trim(),
        userFilter: draft.userFilter.trim(),
        displayNameAttr: draft.displayNameAttr.trim(),
        groupAttr: draft.groupAttr.trim(),
        nestedGroups: draft.nestedGroups,
        createAccounts: draft.createAccounts,
        offlineGraceHours: draft.offlineGraceHours,
        enabled: draft.enabled,
        // Left out entirely when nothing was typed, so an unrelated change — a
        // grace period, the "enabled" switch — cannot accidentally clear a
        // password that was never touched. The server's own rule for
        // "unchanged" is exactly this: the field's absence.
        ...(bindPassword.trim() !== '' ? { bindPassword } : {}),
      })
      onSaved(directory)
    } catch (err) {
      // The drawer stays open and says why, beside the fields that caused it.
      setError(refusal(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <InnerPage
      wide
      back={back}
      title="Directory connection"
      onClose={onClose}
      onSubmit={() => void save()}
      submitLabel="Save"
      valid={!problem}
      busy={busy}
      error={error || undefined}
    >
      {saved?.bindPasswordUnreadable && (
        <Notice tone="warning">Saved password unreadable on this server. Enter it again.</Notice>
      )}

      <Toggle title="Directory sign-in" checked={draft.enabled} onChange={(enabled) => set({ enabled })} />

      <fieldset className="dir-group-fields">
        <legend>Server</legend>
        <label className="field">
          <span>Address</span>
          <Input
            fullWidth
            className="dir-mono-input"
            value={draft.url}
            onChange={(e) => set({ url: e.target.value })}
            placeholder="ldaps://dc.bank.az"
            aria-invalid={Boolean(problem)}
          />
        </label>
        {problem && <p className="field-error">{problem}</p>}
        {(plainLdap || draft.startTls) && (
          <Toggle title="StartTLS" checked={draft.startTls} onChange={(startTls) => set({ startTls })} />
        )}
        <details className="dir-more" open={draft.caPem !== ''}>
          <summary>Certificate authority</summary>
          <TextArea
            fullWidth
            className="dir-textarea"
            rows={4}
            value={draft.caPem}
            onChange={(e) => set({ caPem: e.target.value })}
            placeholder="-----BEGIN CERTIFICATE-----"
          />
        </details>
      </fieldset>

      <fieldset className="dir-group-fields">
        <legend>Checked by</legend>
        <RadioGroup className="dir-modes" name="mode" aria-label="Checked by" value={mode} onChange={(m) => switchMode(m as typeof mode)}>
          <Radio value="template">
            <Radio.Content>
              <Radio.Control>
                <Radio.Indicator />
              </Radio.Control>
              <span className="dir-modes__text">
                <strong title="Nothing stored. Best for Active Directory.">Own account</strong>
              </span>
            </Radio.Content>
          </Radio>
          <Radio value="service">
            <Radio.Content>
              <Radio.Control>
                <Radio.Indicator />
              </Radio.Control>
              <span className="dir-modes__text">
                <strong title="Whoever holds it can sign in as anyone.">Service account</strong>
              </span>
            </Radio.Content>
          </Radio>
        </RadioGroup>
        {mode === 'service' ? (
          <>
            <label className="field">
              <span>Service account</span>
              <Input
                fullWidth
                className="dir-mono-input"
                value={draft.bindDn}
                onChange={(e) => set({ bindDn: e.target.value })}
                placeholder="CN=svc-qms,OU=Service accounts,DC=bank,DC=az"
              />
            </label>
            {/* Never prefilled: the password does not leave the server. */}
            <label className="field">
              <span>Password</span>
              <Input
                fullWidth
                type="password"
                autoComplete="new-password"
                value={bindPassword}
                onChange={(e) => setBindPassword(e.target.value)}
                placeholder={saved?.bindPasswordSet ? 'Saved. Leave empty to keep it.' : undefined}
              />
              {!saved?.bindPasswordSet && <small>Required.</small>}
            </label>
          </>
        ) : (
          <label className="field">
            <span title="Must contain {username}.">Sign-in name</span>
            <Input
              fullWidth
              className="dir-mono-input"
              value={draft.userTemplate}
              onChange={(e) => set({ userTemplate: e.target.value })}
              placeholder="{username}@bank.az"
            />
          </label>
        )}
      </fieldset>

      <fieldset className="dir-group-fields">
        <legend>Search</legend>
        <label className="field">
          <span>Look in</span>
          <Input
            fullWidth
            className="dir-mono-input"
            value={draft.baseDn}
            onChange={(e) => set({ baseDn: e.target.value })}
            placeholder="OU=Staff,DC=bank,DC=az"
          />
        </label>
        <label className="field">
          <span title="In brackets, with {username}.">Search filter</span>
          <Input
            fullWidth
            className="dir-mono-input"
            value={draft.userFilter}
            onChange={(e) => set({ userFilter: e.target.value })}
            placeholder="(sAMAccountName={username})"
          />
        </label>
        <div className="pk-fields">
          <label className="field">
            <span>Name attribute</span>
            <Input fullWidth value={draft.displayNameAttr} onChange={(e) => set({ displayNameAttr: e.target.value })} />
          </label>
          <label className="field">
            <span>Groups attribute</span>
            <Input fullWidth value={draft.groupAttr} onChange={(e) => set({ groupAttr: e.target.value })} />
          </label>
        </div>
        <Toggle title="Nested groups" checked={draft.nestedGroups} onChange={(nestedGroups) => set({ nestedGroups })} />
      </fieldset>

      <fieldset className="dir-group-fields">
        <legend>At sign-in</legend>
        <Toggle
          title="Create on first sign-in"
          checked={draft.createAccounts}
          onChange={(createAccounts) => set({ createAccounts })}
        />
        <label className="field">
          <span title="Last password keeps working. 0 is off, at most 720.">Directory down</span>
          <span className="dir-unit">
            <Input
              type="number"
              min={0}
              max={720}
              value={Number.isFinite(draft.offlineGraceHours) ? draft.offlineGraceHours : ''}
              onChange={(e) => set({ offlineGraceHours: e.target.valueAsNumber })}
            />
            hours
          </span>
        </label>
      </fieldset>
    </InnerPage>
  )
}

// ---------------------------------------------------------------------------
// Try a sign-in.

export function TestSignIn({
  organisation,
  branches,
  rows,
  roles,
}: {
  organisation?: string
  branches: Branch[]
  /** The group mappings as saved: each of the person's groups is shown with what
   *  it gives. The verdict itself is the server's. */
  rows: MappingRow[]
  /** Names a mapping's roleId; the verdict's own role the server resolved. */
  roles: OrgRole[]
}) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<DirectoryTestResult | null>(null)

  async function tryIt(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    setResult(null)
    try {
      setResult(await api.testDirectory(organisation, username, password))
    } catch (err) {
      setError(refusal(err))
    } finally {
      setBusy(false)
      // The password just tried is not kept around for a second attempt to fail
      // silently with — the same reason a sign-in screen clears it.
      setPassword('')
    }
  }

  const branchName = (id: string) => branches.find((b) => b.id === id)?.name ?? id
  const roleName = (id?: string) => {
    const r = roles.find((x) => x.id === id)
    return r ? roleLabel(r.roleKey, r.name, 'en') : id ?? ''
  }
  const gives = (row: MappingRow) =>
    row.gives === 'role' ? roleName(row.roleId) : branchName(row.branchId ?? '')
  const givenBy = (group: string) => rows.filter((r) => normaliseGroup(r.group) === normaliseGroup(group))
  const verdictBranches = (result?.branchIds ?? []).map(branchName)

  return (
    <SettingsCard title="Try a sign-in">
      <SettingBlock>
        {/* Not the owner's own sign-in: Chrome ignores "off" on a password field, not "new-password". */}
        <form className="dir-try" onSubmit={tryIt}>
          <label className="field">
            <span>Username</span>
            <Input fullWidth autoComplete="off" value={username} onChange={(e) => setUsername(e.target.value)} />
          </label>
          <label className="field">
            <span>Password</span>
            <Input
              fullWidth
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <Button variant="primary" type="submit" isDisabled={busy || !username || !password}>
            Try
          </Button>
        </form>

        {error && <Notice className="dir-tryerr">{error}</Notice>}

        {result && (
          <div className="dir-found">
            <div className="dir-found__who">
              <span className="people-avatar">
                <Icon name="user" size={20} />
              </span>
              <span>
                <strong>{result.person.displayName}</strong>
                <span className="dir-mono">{result.person.dn}</span>
              </span>
            </div>
            {result.person.groups.length > 0 ? (
              <ul className="dir-found__groups">
                {result.person.groups.map((group, i) => {
                  const hits = givenBy(group)
                  return (
                    <li key={i} data-hit={hits.length > 0 || undefined}>
                      <span className="dir-found__g" title={group}>
                        {shortGroup(group)}
                      </span>
                      <span className="dir-found__gives">
                        {hits.length === 0 ? (
                          <span className="pk-quiet">Ignored</span>
                        ) : (
                          hits.map((row) => (
                            <Chip key={row.key} size="sm" variant="secondary">
                              {gives(row)}
                            </Chip>
                          ))
                        )}
                      </span>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="pk-quiet">No groups.</p>
            )}
            {result.refusal ? (
              <p className="dir-verdict">
                <Icon name="alert" size={18} />
                <span>{result.refusal}</span>
              </p>
            ) : (
              <p className="dir-verdict" data-ok>
                <Icon name="check" size={18} />
                <span>
                  Signs in as <strong>{roleLabel(result.roleKey, result.roleName, 'en')}</strong>
                  {verdictBranches.length > 0 && (
                    <>
                      , in <strong>{verdictBranches.join(', ')}</strong>
                    </>
                  )}
                  .
                </span>
              </p>
            )}
          </div>
        )}
      </SettingBlock>
    </SettingsCard>
  )
}
